// src/features/statusdiario/StatusDiarioPage.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./StatusDiarioPage.css";
import SdTempoTimer from "./SdTempoTimer";

/* =========================
   Helpers
========================= */
function todayYMD_BR() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function isBlankish(v) {
  const s = String(v ?? "").trim();
  return !s || s === "—" || s === "-";
}

function chooseRealOrProg(real, prog, fallback = "—") {
  if (!isBlankish(real)) return real;
  if (!isBlankish(prog)) return prog;
  return fallback;
}

function toNiceCase(value) {
  if (value == null) return "—";
  const s = String(value).trim();
  if (!s || s === "—" || s === "-") return "—";
  const small = new Set(["de", "da", "do", "das", "dos", "e", "em", "para", "por", "a", "o"]);
  return s
    .toLowerCase()
    .split(/([ -])/g)
    .map((token, i, arr) => {
      if (token === " " || token === "-") return token;
      if (i === 0 || (arr[i - 1] && (arr[i - 1] === "-" || arr[i - 1] === " "))) {
        return token.charAt(0).toUpperCase() + token.slice(1);
      }
      return small.has(token) ? token : token.charAt(0).toUpperCase() + token.slice(1);
    })
    .join("");
}

function parsePtBrNumber(val) {
  if (val == null) return null;
  const s = String(val).trim();
  if (!s || s === "—" || s === "-") return null;
  const n = Number(s.replace(/\s/g, "").replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function formatQtdMaybe(v) {
  if (v == null || v === "" || v === "—" || v === "-") return "—";
  const s = String(v).trim();
  const n = Number(s.replace(/\./g, "").replace(",", "."));
  if (Number.isNaN(n)) return s;
  if (Number.isInteger(n)) return new Intl.NumberFormat("pt-BR").format(n);
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 3 }).format(n);
}

/** sempre HH:MM */
function toHMOnly(v) {
  if (v == null || v === "") return "—";
  const s = String(v).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  const m2 = s.match(/(\d{1,2}):(\d{2})/);
  if (m2) return `${m2[1].padStart(2, "0")}:${m2[2]}`;
  return s || "—";
}

/* tempo (para inferir status running/paused/waiting SEM ficar reordenando por segundo) */
function isZeroLike(raw) {
  if (raw == null) return true;
  if (typeof raw === "number") return raw === 0;
  const s = String(raw).trim().toLowerCase();
  if (!s) return true;
  if (["0", "00", "00:00", "00:00:00", "0000-00-00 00:00:00", "—", "-"].includes(s)) return true;
  if (/^0{1,2}:0{2}(:0{2})?(\.0+)?$/.test(s)) return true;
  return false;
}

function parseToSeconds(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v)) return Math.trunc(v) * 60;
  const cleaned = String(v).trim();
  if (!cleaned || cleaned === "—" || cleaned === "-") return null;
  const parts = cleaned.split(":").map((n) => parseInt(n, 10));
  if (parts.some((x) => Number.isNaN(x))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 3600 + parts[1] * 60;
  return parts[0] * 60;
}

function parseToTsWithDay(value, refDay) {
  if (value == null) return null;

  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }

  let raw = String(value).trim();
  if (!raw || isZeroLike(raw)) return null;

  raw = raw.replace(/\.\d+$/, "");

  if (/^\d{10}$/.test(raw)) return parseInt(raw, 10) * 1000;
  if (/^\d{13}$/.test(raw)) return parseInt(raw, 10);

  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
    const [hh, mm, ss = "00"] = raw.split(":");
    const day = refDay || todayYMD_BR();
    const d = new Date(`${day}T${String(hh).padStart(2, "0")}:${mm}:${ss}`);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }

  if (/^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
    const d = new Date(raw.replace(" ", "T"));
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }

  const d1 = new Date(raw);
  if (!Number.isNaN(d1.getTime())) return d1.getTime();

  const d2 = new Date(raw.replace(" ", "T") + "Z");
  if (!Number.isNaN(d2.getTime())) return d2.getTime();

  return null;
}

/* hash simples e estável p/ diferenciar keys duplicadas */
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/* =========================
   FLIP (só quando a ordem muda)
========================= */
function useFlipList(scopeKey, itemKeys, durationMs = 680) {
  const nodeMapRef = useRef(new Map());
  const prevRectsRef = useRef(new Map());
  const raf1Ref = useRef(0);
  const raf2Ref = useRef(0);
  const lastScopeRef = useRef(scopeKey);
  const tokenRef = useRef(new Map()); // key -> token

  const keysSig = useMemo(() => itemKeys.join("|"), [itemKeys]);

  useLayoutEffect(() => {
    // troca escopo: zera medições
    if (lastScopeRef.current !== scopeKey) {
      lastScopeRef.current = scopeKey;
      prevRectsRef.current = new Map();
      for (const el of nodeMapRef.current.values()) {
        if (!el) continue;
        el.classList.remove("sd-flip-moving");
        el.style.transform = "";
        el.style.transition = "";
        el.style.zIndex = "";
        el.style.willChange = "";
        el.dataset.flipToken = "";
      }
      return;
    }

    if (raf1Ref.current) cancelAnimationFrame(raf1Ref.current);
    if (raf2Ref.current) cancelAnimationFrame(raf2Ref.current);

    const prevRects = prevRectsRef.current || new Map();
    const nextRects = new Map();

    // mede e aplica inversão
    for (const key of itemKeys) {
      const el = nodeMapRef.current.get(key);
      if (!el) continue;

      const next = el.getBoundingClientRect();
      nextRects.set(key, next);

      const prev = prevRects.get(key);
      if (!prev) continue;

      const dx = prev.left - next.left;
      const dy = prev.top - next.top;
      if (!dx && !dy) continue;

      const t = (tokenRef.current.get(key) || 0) + 1;
      tokenRef.current.set(key, t);
      el.dataset.flipToken = String(t);

      el.classList.add("sd-flip-moving");
      el.style.willChange = "transform";
      el.style.zIndex = "5";

      el.style.transition = "none";
      el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
    }

    // anima p/ destino
    raf1Ref.current = requestAnimationFrame(() => {
      raf2Ref.current = requestAnimationFrame(() => {
        for (const key of itemKeys) {
          const el = nodeMapRef.current.get(key);
          if (!el) continue;
          if (!el.style.transform) continue;

          el.style.transition = `transform ${durationMs}ms var(--sd-ease)`;
          el.style.transform = "";

          const token = el.dataset.flipToken;
          window.setTimeout(() => {
            const el2 = nodeMapRef.current.get(key);
            if (!el2) return;
            if (String(el2.dataset.flipToken || "") !== String(token || "")) return;
            el2.classList.remove("sd-flip-moving");
            el2.style.zIndex = "";
            el2.style.willChange = "";
            el2.dataset.flipToken = "";
          }, durationMs + 80);
        }
      });
    });

    prevRectsRef.current = nextRects;

    return () => {
      if (raf1Ref.current) cancelAnimationFrame(raf1Ref.current);
      if (raf2Ref.current) cancelAnimationFrame(raf2Ref.current);
    };
  }, [scopeKey, keysSig, itemKeys, durationMs]);

  const setNode = (key) => (el) => {
    if (el) nodeMapRef.current.set(key, el);
    else nodeMapRef.current.delete(key);
  };

  return { setNode };
}

/* =========================
   Normalização de keys (corrige warning de key duplicada)
========================= */
function buildBaseKey(r, day) {
  const k = String(r?.key ?? "").trim();
  if (k) return k;

  const id =
    String(r?.id_display ?? r?.codigo_unico ?? r?.codigo ?? r?.id ?? r?.ativo ?? "linha").trim() ||
    "linha";

  return `${day}::${id}`;
}

function buildIdentitySig(r) {
  // NÃO usa label do timer; usa campos “de identidade” / estáticos
  const parts = [
    r?.gerencia,
    r?.ativo,
    r?.atividade_prog,
    r?.atividade_real,
    r?.horario_prog,
    r?.horario_real,
    r?.tempo_do_intervalo,
    r?.previsao_inicio_intervalo,
    r?.local_partida,
    r?.sb_trabalho,
    r?.km,
    r?.quantidade_prog,
    r?.quantidade_real,
    r?.detalhamento,
    r?.primeiro_inicio,
    r?.primeiro_fim,
    r?.segundo_inicio,
    r?.segundo_fim,
    r?.terceiro_inicio,
    r?.terceiro_fim,
    r?.status_wf3,
    r?.rowClass,
  ]
    .map((x) => String(x ?? "").trim())
    .join("|");

  return parts;
}

function normalizeRowsWithStableUniqueKeys(rows, day) {
  const list = Array.isArray(rows) ? rows : [];

  // 1) conta ocorrências do baseKey
  const counts = new Map();
  const baseKeys = list.map((r) => {
    const bk = buildBaseKey(r, day);
    counts.set(bk, (counts.get(bk) || 0) + 1);
    return bk;
  });

  // 2) gera key final:
  //    - se baseKey é único: usa baseKey (estável)
  //    - se baseKey repete: usa baseKey + hash(signature) (único e estável)
  const out = [];
  const seenFinal = new Set();

  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    const bk = baseKeys[i];
    const needsHash = (counts.get(bk) || 0) > 1;

    const sig = needsHash ? buildIdentitySig(r) : "";
    const hk = needsHash ? fnv1a(sig) : "";
    const finalKey = needsHash ? `${bk}::${hk}` : bk;

    // se for duplicata exata (mesma finalKey), ignora (evita duplicar linhas iguais)
    if (seenFinal.has(finalKey)) continue;
    seenFinal.add(finalKey);

    out.push({ ...r, __k: finalKey });
  }

  return out;
}

/* =========================
   Componente
========================= */
export default function StatusDiarioPage({ onBack, rows = [], dayKey, setDayKey, loading }) {
  const currentDay = typeof dayKey === "string" && dayKey ? dayKey : todayYMD_BR();
  const applyDay = setDayKey;

  // UI day com debounce
  const [uiDay, setUiDay] = useState(currentDay);
  const dayTimerRef = useRef(null);

  useEffect(() => setUiDay(currentDay), [currentDay]);
  useEffect(() => () => dayTimerRef.current && clearTimeout(dayTimerRef.current), []);

  const onChangeDay = useCallback(
    (next) => {
      const v = next || todayYMD_BR();
      setUiDay(v);
      if (!applyDay) return;
      if (dayTimerRef.current) clearTimeout(dayTimerRef.current);
      dayTimerRef.current = setTimeout(() => applyDay(v), 220);
    },
    [applyDay]
  );

  // ✅ rows normalizadas com key única e estável
  const rowsSafe = useMemo(() => {
    return normalizeRowsWithStableUniqueKeys(rows, currentDay);
  }, [rows, currentDay]);

  // ✅ gerências (UMA ÚNICA VEZ — sem duplicar variável)
  const allGerencias = useMemo(() => {
    return Array.from(new Set(rowsSafe.map((r) => r?.gerencia).filter(Boolean))).sort((a, b) =>
      String(a).localeCompare(String(b), "pt-BR")
    );
  }, [rowsSafe]);

  const [gerFilter, setGerFilter] = useState("");
  useEffect(() => {
    if (gerFilter && !allGerencias.includes(gerFilter)) setGerFilter("");
  }, [gerFilter, allGerencias]);

  const filtered = useMemo(() => {
    if (!gerFilter) return rowsSafe;
    return rowsSafe.filter((r) => (r?.gerencia || "") === gerFilter);
  }, [rowsSafe, gerFilter]);

  // ✅ ordenação ESTÁVEL
  // Ordem: running (timer mais urgente no topo) → waiting → paused → finalizado → reprogramado (bottom)
  const ordered = useMemo(() => {
    const nowTs = Date.now();

    const enrich = (r) => {
      const rowClass = String(r?.rowClass || "");
      const statusText = [
        r?.status_wf3,
        r?.status,
        r?.status_programacaod1,
        r?.status_programacao,
        r?.status_programacao_d1,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const isReprog = rowClass.includes("sd-flag--reprogramar") || statusText.includes("reprogram");
      const isFinal = rowClass.includes("sd-flag--finalizado") || statusText.includes("finaliz");

      const target = r?.meta_tempo_intervalo ?? r?.tempo_do_intervalo ?? r?.tempo_intervalo ?? "";
      const metaSeconds = parseToSeconds(target);

      const p1s = parseToTsWithDay(r?.primeiro_inicio ?? r?.primeiro_inicio_at, currentDay);
      const p1e = parseToTsWithDay(r?.primeiro_fim, currentDay);
      const p2s = parseToTsWithDay(r?.segundo_inicio, currentDay);
      const p2e = parseToTsWithDay(r?.segundo_fim, currentDay);
      const p3s = parseToTsWithDay(r?.terceiro_inicio, currentDay);
      const p3e = parseToTsWithDay(r?.terceiro_fim, currentDay);

      // status primário (regra antiga): running/paused/waiting, com flags final/reprog
      let status = "waiting";
      if (isFinal) status = "finalizado";
      else if (isReprog) status = "reprogramar";
      else if (metaSeconds != null && metaSeconds > 0 && p1s != null) {
        const last = [
          { s: p3s, e: p3e },
          { s: p2s, e: p2e },
          { s: p1s, e: p1e },
        ].find((x) => x.s != null);
        status = last && last.e == null ? "running" : "paused";
      } else {
        status = "waiting";
      }
      // injeta estado PRE vindo do backend (sem alterar primário dos demais)
      if (String(r?.timer_status || "") === "pre") {
        status = "pre";
      }

      // ✅ calcula tempo restante (para ordenação por urgência)
      let remainingSec = Infinity; // quanto menor, mais urgente
      if (status === "pre" && Number.isFinite(r?.timer_remaining_signed)) {
        remainingSec = r.timer_remaining_signed;
      } else if (metaSeconds != null && metaSeconds > 0 && p1s != null) {
        const pairs = [
          { start: p1s, end: p1e },
          { start: p2s, end: p2e },
          { start: p3s, end: p3e },
        ];
        let consumedMs = 0;
        for (const { start, end } of pairs) {
          if (start == null) continue;
          const e = end == null ? nowTs : end;
          if (e > start) consumedMs += e - start;
        }
        const consumedSec = Math.floor(consumedMs / 1000);
        remainingSec = metaSeconds - consumedSec;
      }

      const isExpired = remainingSec <= 0;

      // ✅ Ordenação solicitada (reprogramar sempre no fim)
      // 1 running exp, 2 running, 3 paused exp, 4 paused, 5 waiting, 6 pre, 90 final, 99 reprog
      let group = 5;
      if (status === "reprogramar") group = 99;
      else if (status === "finalizado") group = 90;
      else if (status === "pre") group = 6;
      else if (status === "waiting") group = 5;
      else if (status === "paused") group = isExpired ? 3 : 4;
      else if (status === "running") group = isExpired ? 1 : 2;

      // tie-breakers estáveis
      const ativoKey = String(r?.ativo ?? "");
      const gerKey = String(r?.gerencia ?? "");
      const horaKey = String(r?.horario_real ?? r?.horario_prog ?? "");

      return { r, group, remainingSec, ativoKey, gerKey, horaKey, status };
    };

    const list = filtered.map(enrich);

    const priority = (x) => {
      // força flag reprogramar no fim mesmo se status falhar
      if (String(x?.r?.rowClass || "").includes("sd-flag--reprogramar")) return 99;
      if (x.status === "reprogramar") return 99;
      return x.group;
    };

    const compare = (a, b) => {
      const pa = priority(a);
      const pb = priority(b);
      if (pa !== pb) return pa - pb;

      // 2) dentro de running/paused/pre/waiting: por tempo restante (menor = mais urgente = topo)
      const shouldSortByTime =
        a.group <= 2 || a.group === 3 || a.group === 4 || a.group === 5 || a.group === 6;
      if (shouldSortByTime && a.remainingSec !== b.remainingSec) {
        return a.remainingSec - b.remainingSec;
      }

      // 3) tie-breakers estáveis
      if (a.ativoKey !== b.ativoKey) return a.ativoKey.localeCompare(b.ativoKey, "pt-BR");
      if (a.gerKey !== b.gerKey) return a.gerKey.localeCompare(b.gerKey, "pt-BR");
      if (a.horaKey !== b.horaKey) return a.horaKey.localeCompare(b.horaKey, "pt-BR");
      return 0;
    };

    list.sort(compare);
    return list;
  }, [filtered, currentDay]);

  // ✅ rows de view (não dependem de "nowTs", então NÃO re-rendera a lista a cada 1s)
  const rowsView = useMemo(() => {
    return ordered.map(({ r, status }) => {
      const atividadeChosen = chooseRealOrProg(r?.atividade_real, r?.atividade_prog, "—");

      const horaRealHM = !isBlankish(r?.horario_real) ? toHMOnly(r?.horario_real) : "—";
      const horaProgHM = !isBlankish(r?.horario_prog) ? toHMOnly(r?.horario_prog) : "—";
      const horarioChosen = chooseRealOrProg(horaRealHM, horaProgHM, "—");

      // QUANTIDADE: só usa REAL quando REAL > 0
      const realNum = parsePtBrNumber(r?.quantidade_real);
      let qtdChosen = r?.quantidade_prog ?? "—";
      if (realNum != null) {
        if (realNum > 0) qtdChosen = r?.quantidade_real;
        else if (isBlankish(r?.quantidade_prog)) qtdChosen = r?.quantidade_real;
      }

      const detBase = (r?.detalhamento ?? "").toString() || "—";
      const det = detBase || "—";

      return {
        key: String(r.__k),
        status,
        paused: status === "paused",
        className: String(r?.rowClass || "").trim(),

        up: r?.gerencia ?? "—",
        ativo: r?.ativo ?? "—",
        atividade: toNiceCase(atividadeChosen),
        horario: horarioChosen,
        qt: formatQtdMaybe(qtdChosen),

        previsao: toHMOnly(r?.previsao_inicio_intervalo ?? r?.previsao ?? "—"),
        previsao_raw: r?.previsao_inicio_intervalo ?? r?.previsao ?? "",
        local_partida: r?.local_partida ?? "—",
        sb_trabalho: r?.sb_trabalho ?? "—",
        km: r?.km ?? "—",
        detalhamento: det,

        // ✅ TIMER: dados do backend, frontend atualiza display localmente
        timer: {
          day: currentDay,
          // dados brutos para o SdTempoTimer recalcular localmente
          target: r?.meta_tempo_intervalo ?? r?.tempo_do_intervalo ?? r?.tempo_intervalo ?? "",
          previsao: r?.previsao_inicio_intervalo ?? r?.previsao ?? "",
          primeiro_inicio: r?.primeiro_inicio ?? r?.primeiro_inicio_at,
          primeiro_fim: r?.primeiro_fim,
          segundo_inicio: r?.segundo_inicio,
          segundo_fim: r?.segundo_fim,
          terceiro_inicio: r?.terceiro_inicio,
          terceiro_fim: r?.terceiro_fim,
          // ainda mantemos os campos calculados pelo backend para eventual telemetria/debug
          timer_status: r?.timer_status ?? "waiting",
          timer_remaining_signed: r?.timer_remaining_signed ?? null,
          timer_meta_seconds: r?.timer_meta_seconds ?? null,
          timer_calculated_at: r?.timer_calculated_at ?? Date.now(),
        },
      };
    });
  }, [ordered, currentDay]);

  // highlight de “mudou”
  const [updatedKeys, setUpdatedKeys] = useState(() => new Set());
  const sigRef = useRef(new Map());

  useEffect(() => {
    const nextSig = new Map();
    const changed = [];

    for (const r of rowsView) {
      // não inclui timer label (muda sozinho no SdTempoTimer)
      const sig = [
        r.up,
        r.ativo,
        r.atividade,
        r.horario,
        r.qt,
        r.tempo?.target,
        r.previsao,
        r.local_partida,
        r.sb_trabalho,
        r.km,
        r.detalhamento,
        r.className,
      ].join("||");

      nextSig.set(r.key, sig);

      const prev = sigRef.current.get(r.key);
      if (prev && prev !== sig) changed.push(r.key);
    }

    sigRef.current = nextSig;
    if (!changed.length) return;

    setUpdatedKeys((prev) => {
      const n = new Set(prev);
      changed.forEach((k) => n.add(k));
      return n;
    });

    const t = setTimeout(() => {
      setUpdatedKeys((prev) => {
        const n = new Set(prev);
        changed.forEach((k) => n.delete(k));
        return n;
      });
    }, 900);

    return () => clearTimeout(t);
  }, [rowsView]);

  // ✅ FLIP só quando itemKeys mudam (ordem muda / filtro muda / payload muda)
  const scopeKey = `day:${currentDay}|ger:${gerFilter || "all"}`;
  const itemKeys = useMemo(() => rowsView.map((r) => r.key), [rowsView]);
  const flip = useFlipList(scopeKey, itemKeys, 680);

  return (
    <div className="sd-page">
      <header className="sd-appbar">
        <div className="sd-appbar__wrap">
          <button
            type="button"
            className="sd-btn sd-btn--menu"
            onClick={() => onBack?.()}
          >
            Menu Principal
          </button>

          <div className="sd-title">ACOMPANHAMENTO DOS INTERVALOS</div>

          <div className="sd-controls">
            <div className="sd-ctlRow">
              <span className="sd-ctlLbl">Data</span>
              <input
                className="sd-date"
                type="date"
                value={uiDay}
                onChange={(e) => onChangeDay(e.target.value)}
              />
            </div>

            <div className="sd-ctlRow">
              <span className="sd-ctlLbl">Gerência</span>
              <select
                className="sd-multi"
                value={gerFilter}
                onChange={(e) => setGerFilter(e.target.value)}
              >
                <option value="">Todas</option>
                {allGerencias.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            {loading ? <span className="sd-loading">Carregando…</span> : null}
          </div>
        </div>
      </header>

      <div className="sd-table">
          <div className="sd-headrow sd-gridCols">
            <div className="sd-hcell">UP</div>
            <div className="sd-hcell">ATIVO</div>
            <div className="sd-hcell">ATIVIDADE</div>
            <div className="sd-hcell">HORÁRIO</div>
            <div className="sd-hcell">PREVISÃO</div>
            <div className="sd-hcell">TEMPO</div>
            <div className="sd-hcell">QUANTIDADE</div>
            <div className="sd-hcell">LOCAL</div>
            <div className="sd-hcell">SB</div>
            <div className="sd-hcell">KM</div>
            <div className="sd-hcell">DETALHAMENTO</div>
          </div>

        <ul className="sd-rows">
          {rowsView.map((r) => {
            const updated = updatedKeys.has(r.key);
            return (
              <li
                key={r.key}
                ref={flip.setNode(r.key)}
                className={`sd-row sd-gridCols ${r.className} ${updated ? "sd-updated" : ""}`.trim()}
                data-status={r.status}
                data-paused={r.paused ? "true" : "false"}
              >
                <div className="sd-cell">{r.up}</div>
                <div className="sd-cell">{r.ativo}</div>

                <div className="sd-cell sd-cell--atividade">{r.atividade}</div>

                <div className="sd-cell">{r.horario}</div>
                <div className="sd-cell">{r.previsao}</div>

                <div className="sd-cell sd-cell--tempo">
                  <SdTempoTimer
                    day={r.timer.day}
                    previsao={r.previsao_raw}
                    target={r.timer.target}
                    primeiro_inicio={r.timer.primeiro_inicio}
                    primeiro_fim={r.timer.primeiro_fim}
                    segundo_inicio={r.timer.segundo_inicio}
                    segundo_fim={r.timer.segundo_fim}
                    terceiro_inicio={r.timer.terceiro_inicio}
                    terceiro_fim={r.timer.terceiro_fim}
                    timer_status={r.timer.timer_status}
                    timer_remaining_signed={r.timer.timer_remaining_signed}
                    timer_meta_seconds={r.timer.timer_meta_seconds}
                    timer_calculated_at={r.timer.timer_calculated_at}
                  />
                </div>

                <div className="sd-cell">{r.qt}</div>

                <div className="sd-cell">{r.local_partida}</div>
                <div className="sd-cell">{r.sb_trabalho}</div>
                <div className="sd-cell">{r.km}</div>
                <div className="sd-cell sd-cell--det">{r.detalhamento}</div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
